import { __decorate } from "tslib";
import { Binding } from "@ortho-next/three-base";
import { LinearFilter, Mesh, PlaneGeometry, ShaderMaterial, Texture, TextureLoader, Vector2, Vector3 } from "three";
import { ToolType } from "../Core/ITool.js";
import { Save } from "../Core/Save.js";
import { ViewType } from "../Models/AppModel.js";
import { InitialFlipType } from "../Models/CaseModel.js";
import { Consts } from "../Utils/Consts.js";
import { transformImage } from "../Utils/ImageUtils.js";
import { Shaders } from "../Utils/Shaders.js";
/**
 * Plane mesh with the xray as texture.
 */
export class Plane extends Mesh {
    constructor(url, viewType, onTextureLoad) {
        super(Consts.emptyGeometry, new ShaderMaterial({
            vertexShader: Shaders.planeVertexShader,
            fragmentShader: Shaders.planeFragmentShader,
            uniforms: {
                brightness: { value: 0 },
                contrast: { value: 0 },
                hFlip: { value: false },
                vFlip: { value: false },
                bottomLeftClip: { value: new Vector2(0, 0) },
                topRightClip: { value: new Vector2(1, 1) },
                map: { value: undefined }
            }
        }));
        this.toolType = ToolType.plane;
        this.widthGeometry = 100;
        this.rotationAfterFlip = 0;
        this.flipType = null;
        this._originalMap = null;
        this.name = "Plane";
        this.interceptedByRaycaster = false;
        this.position.z = -100;
        this.loadImage(url, onTextureLoad);
        this.bindProperties(viewType);
    }
    async applyHorizontalFlip() {
        // this breaks the crop, but we flip always before crop
        if (this.flipType === null)
            this.flipType = InitialFlipType.horizontal;
        else if (this.flipType === InitialFlipType.horizontal)
            this.flipType = null;
        else if (this.flipType === InitialFlipType.vertical)
            this.flipType = InitialFlipType.rotate180;
        else if (this.flipType === InitialFlipType.rotate180)
            this.flipType = InitialFlipType.vertical;
        const invertRotation = this.flipType === InitialFlipType.rotate180 || this.flipType === null;
        this.rotationAfterFlip += invertRotation ? -this.rotation.z : this.rotation.z;
        const img = await transformImage(this._originalMap.image, this.rotationAfterFlip, this.flipType);
        this.loadCanvasImage(img);
        this.rotation.z = 0;
    }
    async applyVerticalFlip() {
        // this breaks the crop, but we flip always before crop
        const map = this.material.uniforms["map"].value;
        const img = await transformImage(map.image, this.rotationAfterFlip, this.flipType);
        this.loadCanvasImage(img);
        if (this.flipType === null)
            this.flipType = InitialFlipType.vertical;
        else if (this.flipType === InitialFlipType.vertical)
            this.flipType = null;
        else if (this.flipType === InitialFlipType.horizontal)
            this.flipType = InitialFlipType.rotate180;
        else if (this.flipType === InitialFlipType.rotate180)
            this.flipType = InitialFlipType.horizontal;
        const invertRotation = this.flipType === InitialFlipType.rotate180 || this.flipType === null;
        this.rotationAfterFlip += invertRotation ? -this.rotation.z : this.rotation.z;
        this.rotation.z = 0;
    }
    apply180Rotation() {
        // this would not work if flipType is vertical or horizontal but it's impossible now because flip is not cumulative
        this.flipType = this.flipType === InitialFlipType.rotate180 ? null : InitialFlipType.rotate180;
        this.rotation.z += Math.PI;
    }
    get horizontalFlip() {
        return this.material.uniforms["hFlip"].value;
    }
    set horizontalFlip(value) {
        this.material.uniforms["hFlip"].value = value;
        this.updateCenterTranslation();
        this.needsRender = true;
    }
    get verticalFlip() {
        return this.material.uniforms["vFlip"].value;
    }
    set verticalFlip(value) {
        this.material.uniforms["vFlip"].value = value;
        this.updateCenterTranslation();
        this.needsRender = true;
    }
    get brightness() {
        return this.material.uniforms["brightness"].value;
    }
    set brightness(value) {
        this.material.uniforms["brightness"].value = value;
        this.needsRender = true;
    }
    get contrast() {
        return this.material.uniforms["contrast"].value;
    }
    set contrast(value) {
        this.material.uniforms["contrast"].value = value;
        this.needsRender = true;
    }
    get clipFromLeft() {
        return this.material.uniforms["bottomLeftClip"].value.x;
    }
    set clipFromLeft(value) {
        this.material.uniforms["bottomLeftClip"].value.x = value;
        this.needsRender = true;
    }
    get clipFromTop() {
        return 1 - this.material.uniforms["topRightClip"].value.y;
    }
    set clipFromTop(value) {
        this.material.uniforms["topRightClip"].value.y = 1 - value;
        this.needsRender = true;
    }
    get clipFromRight() {
        return 1 - this.material.uniforms["topRightClip"].value.x;
    }
    set clipFromRight(value) {
        this.material.uniforms["topRightClip"].value.x = 1 - value;
        this.needsRender = true;
    }
    get clipFromBottom() {
        return this.material.uniforms["bottomLeftClip"].value.y;
    }
    set clipFromBottom(value) {
        this.material.uniforms["bottomLeftClip"].value.y = value;
        this.needsRender = true;
    }
    bindProperties(viewType) {
        const propertyName = viewType === ViewType.AP ? "scaleFactorAP" : "scaleFactorLT";
        Binding.createCustom(`${this.id}.scale`, m => m[propertyName], (v) => {
            this.scale.setScalar(v);
            this.updateCenterTranslation();
            this.updateMatrixWorld(true); // necessary for camera controls default zoom //todo capire
        }, [propertyName]);
    }
    /**
     * Gets vertices of not cropped plane.
     * @param forceZ force all vertices Z axis.
     */
    getNoClippedVertices(forceZ) {
        const positions = this.geometry.getAttribute("position").array;
        const vertices = {
            A: this.localToWorld(new Vector3(positions[0], positions[1], positions[2])),
            B: this.localToWorld(new Vector3(positions[3], positions[4], positions[5])),
            C: this.localToWorld(new Vector3(positions[9], positions[10], positions[11])),
            D: this.localToWorld(new Vector3(positions[6], positions[7], positions[8]))
        };
        this.forceZ(forceZ, vertices);
        return vertices;
    }
    /**
     * Gets vertices of cropped plane.
     * @param forceZ force all vertices Z axis.
     */
    getVertices(forceZ) {
        this.updateMatrixWorld(true);
        const ret = this.getNoClippedVertices(0);
        const topTransl = ret.A.clone().lerp(ret.D, this.verticalFlip ? this.clipFromBottom : this.clipFromTop).sub(ret.A);
        const leftTransl = ret.A.clone().lerp(ret.B, this.horizontalFlip ? this.clipFromRight : this.clipFromLeft).sub(ret.A);
        const bottomTransl = ret.C.clone().lerp(ret.B, this.verticalFlip ? this.clipFromTop : this.clipFromBottom).sub(ret.C);
        const rightTransl = ret.C.clone().lerp(ret.D, this.horizontalFlip ? this.clipFromLeft : this.clipFromRight).sub(ret.C);
        ret.A.add(topTransl).add(leftTransl);
        ret.B.add(topTransl).add(rightTransl);
        ret.C.add(bottomTransl).add(rightTransl);
        ret.D.add(bottomTransl).add(leftTransl);
        this.forceZ(forceZ, ret);
        return ret;
    }
    forceZ(value, vertices) {
        if (value !== undefined) {
            vertices.A.z = value;
            vertices.B.z = value;
            vertices.C.z = value;
            vertices.D.z = value;
        }
    }
    /**
     * Moves the plane center to space origin (all 0 coords).
     */
    updateCenterTranslation() {
        if (!this.geometry.getAttribute("position"))
            return;
        const { A, C } = this.getVertices(0);
        const newCenter = A.clone().lerp(C, 0.5).setZ(0);
        this.position.sub(newCenter);
    }
    /**
     * Load a new image and dispose the old one.
     */
    loadImage(url, onTextureLoad) {
        this.material.uniforms["map"].value?.dispose();
        this.material.uniforms["map"].value = undefined;
        this._originalMap = null;
        this.needsRender = true;
        new TextureLoader().load(url, (texture) => {
            const image = texture.image;
            this.textureWidth = image.width;
            this.textureHeight = image.height;
            texture.minFilter = LinearFilter;
            this.updateGeometry();
            this.material.uniforms["map"].value = texture;
            this.needsRender = true;
            this._originalMap = texture;
            onTextureLoad?.();
        });
    }
    loadCanvasImage(canvas) {
        const oldMap = this.material.uniforms["map"].value;
        if (oldMap && oldMap !== this._originalMap)
            oldMap.dispose();
        const texture = new Texture(canvas);
        this.textureWidth = canvas.width;
        this.textureHeight = canvas.height;
        texture.minFilter = LinearFilter;
        texture.needsUpdate = true;
        this.updateGeometry(100 * canvas.width / this._originalMap.width);
        this.material.uniforms["map"].value = texture;
        this.needsRender = true;
    }
    /**
     * Reset flip and rotation
     */
    reset() {
        this.brightness = 0;
        this.contrast = 0;
        this.clipFromTop = 0;
        this.clipFromBottom = 0;
        this.clipFromLeft = 0;
        this.clipFromRight = 0;
        this.verticalFlip = false;
        this.horizontalFlip = false;
        this.rotation.z = 0;
        this.position.setX(0).setY(0);
        this.rotationAfterFlip = 0;
        this.flipType = null;
        this.material.uniforms["map"].value = this._originalMap;
        this._originalMap = null;
        this.widthGeometry = 100;
    }
    getPositionsFromPixels(points) {
        const coords = [];
        const vFlip = this.flipType === InitialFlipType.vertical || this.flipType === InitialFlipType.rotate180;
        const hFlip = this.flipType === InitialFlipType.horizontal || this.flipType === InitialFlipType.rotate180;
        const textureWidth = this._originalMap?.width ?? this.textureWidth;
        const textureHeight = this._originalMap?.height ?? this.textureHeight;
        for (const point of points) {
            let normalizedX = (point.x / textureWidth) - 0.5;
            let normalizedY = (1 - point.y / textureHeight) - 0.5;
            if (hFlip)
                normalizedX *= -1;
            if (vFlip)
                normalizedY *= -1;
            const invertRotation = this.flipType === InitialFlipType.horizontal || this.flipType === InitialFlipType.vertical;
            let currentRotation = this.rotation.z;
            if (invertRotation) {
                currentRotation *= -1;
            }
            else if (this.flipType === InitialFlipType.rotate180) {
                currentRotation -= Math.PI;
            }
            const rotation = this.rotationAfterFlip + currentRotation;
            const originalWidthGeometry = 100;
            const originalHeightGeometry = originalWidthGeometry * (textureHeight / textureWidth);
            const baseVertex = new Vector3(normalizedX * originalWidthGeometry, normalizedY * originalHeightGeometry);
            const vertex = baseVertex.applyAxisAngle(Consts.planeNormal, invertRotation ? -rotation : rotation).add(this.position);
            coords.push(vertex);
        }
        return coords;
    }
    resetGarageFlip() {
        this.rotationAfterFlip = 0;
        this.flipType = null;
        this.textureWidth = this._originalMap?.width ?? this.textureWidth;
        this.textureHeight = this._originalMap?.height ?? this.textureHeight;
        this.updateGeometry();
        const currentTexture = this.material.uniforms["map"].value;
        if (currentTexture !== this._originalMap) {
            currentTexture?.dispose();
        }
        this.material.uniforms["map"].value = this._originalMap;
    }
    updateGeometry(width = 100) {
        const aspectRatio = this.textureHeight / this.textureWidth;
        this.widthGeometry = width;
        this.heightGeometry = this.widthGeometry * aspectRatio;
        const geoParams = this.geometry?.parameters ?? {};
        if (geoParams.height === this.heightGeometry && geoParams.width === this.widthGeometry)
            return;
        this.geometry?.dispose();
        this.geometry = new PlaneGeometry(this.widthGeometry, this.heightGeometry, 1, 1);
    }
}
__decorate([
    Save("uniforms.vFlip.value", "uniforms.hFlip.value", "uniforms.brightness.value", "uniforms.contrast.value", "uniforms.topRightClip.value", "uniforms.bottomLeftClip.value")
], Plane.prototype, "material", void 0);
__decorate([
    Save()
], Plane.prototype, "position", void 0);
__decorate([
    Save()
], Plane.prototype, "rotation", void 0);
//# sourceMappingURL=Plane.js.map